import type {ReactElement} from 'react';
import React, {useEffect, useMemo, useState} from 'react';
import {ItemHeaderTab} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import {ContextualMenu} from '@Components/contextual-menu';
import {ItemPositionContextualOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-position-contextual-options';
import {PosterItemControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls';
import {BaseSlideshowEditOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-slideshow-edit-options';
import {SlideShowPanels} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-slideshow-options/poster-editing-slideshow-options.types';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import {TextSlideBackgroundColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-background-color-panel';
import {TextslideFillColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/textslide-fill-color-panel';
import {SlideitemCustomShadowColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideitem-custom-shadow-color-panel';
import {TextslideOutlineColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/textslide-outline-color-panel';
import {SlideitemTintColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideitem-tint-color-panel';
import {SlideitemRemoveColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideitem-remove-color-panel';
import {SlideitemMutliplyColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideitem-mutliply-color-panel';
import {ItemLockInformationBox} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-lock-information-box';
import {TextSlideFontSelector} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-font-selector';
import {SlideItemGlowColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/slide-item-glow-color-panel';
import {TextSlideListColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-list-color-panel';
import {SlideshowBlockTransitionColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-block-transition-color-panel';
import type {ImageSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {TextSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/text-slide-item.class';
import {LIST_TYPES} from '@PosterWhiteboard/items/text-item/text-list';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {ItemIntroAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-intro-animation-panel/item-intro-animation-panel';
import {ItemOutroAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-outro-animation-panel/item-outro-animation-panel';
import {ContextualMenuContentKeys} from '@Components/poster-editor/components/poster-editing-side-panel/components/contextual-options/contextual-options.types';
import {useGetShadowOptionsObjectOfActiveSlideItem} from '@Hooks/poster-editor/useGetShadowOptionsObjectOfActiveSlideItem';
import {ShadowOrGlowType} from '@postermywall/fabricjs-2';

export function PosterEditingSlideshowOptions(): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const [currentItemHeaderTab, setCurrentItemHeaderTab] = useState(ItemHeaderTab.EDIT);
  const [currentlySelectedPanel, setCurrentlySelectedPanel] = useState(SlideShowPanels.DEFAULT);
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlideShadow = useGetShadowOptionsObjectOfActiveSlideItem();

  if (activeSlideShadow === undefined) {
    throw new Error('active slide item does not have shadow');
  }

  if (!activeItem) {
    throw new Error('unable to show slideshow options because active item does not exist');
  }

  if (!activeItem.isSlideshow() || activeSlideUID === undefined) {
    throw new Error('Type Mismatch: expected a slideshow item');
  }

  const activeSlide = activeItem.getSelectedSlide();

  useEffect((): void => {
    setCurrentlySelectedPanel(SlideShowPanels.DEFAULT);
  }, [activeSlide?.uid]);

  const imageSlide = activeSlide as ImageSlideItem;
  const textSlide = activeSlide as TextSlideItem;

  useEffect((): void => {
    if (
      !(activeSlideShadow?.shadowOrGlowType === ShadowOrGlowType.LIGHT_GLOW || activeSlideShadow?.shadowOrGlowType === ShadowOrGlowType.STRONG_GLOW) &&
      currentlySelectedPanel === SlideShowPanels.GLOW
    ) {
      closePanel();
    }
    if (activeSlideShadow?.shadowOrGlowType !== ShadowOrGlowType.CUSTOM_SHADOW && currentlySelectedPanel === SlideShowPanels.SHADOW) {
      closePanel();
    }
  }, [activeSlideShadow?.shadowOrGlowType]);

  useEffect((): void => {
    if (!imageSlide?.effects?.tint && currentlySelectedPanel === SlideShowPanels.TINT) {
      closePanel();
    }
  }, [imageSlide?.effects?.tint]);

  useEffect((): void => {
    if (!textSlide?.textStyles?.stroke && currentlySelectedPanel === SlideShowPanels.OUTLINE) {
      closePanel();
    }
  }, [textSlide?.textStyles?.stroke]);

  useEffect((): void => {
    if (textSlide?.list?.type === LIST_TYPES.NONE && currentlySelectedPanel === SlideShowPanels.LIST_COLOR) {
      closePanel();
    }
  }, [textSlide?.list?.type]);

  useEffect((): void => {
    if (textSlide?.background?.fill.fillType === FillTypes.NONE && currentlySelectedPanel === SlideShowPanels.BACKGROUND_COLOR) {
      closePanel();
    }
  }, [textSlide?.background?.fill.fillType]);

  useEffect((): void => {
    if (!imageSlide?.effects?.multiply && currentlySelectedPanel === SlideShowPanels.MULTIPLY) {
      closePanel();
    }
  }, [imageSlide?.effects?.multiply]);

  useEffect((): void => {
    if (!imageSlide?.effects?.removeColor && currentlySelectedPanel === SlideShowPanels.REMOVE_COLOR) {
      closePanel();
    }
  }, [imageSlide?.effects?.removeColor]);

  useEffect((): void => {
    if (!imageSlide?.effects?.removeColor && currentlySelectedPanel === SlideShowPanels.REMOVE_COLOR) {
      closePanel();
    }
  }, [imageSlide?.effects?.removeColor]);

  const getHeaderText = (): string => {
    if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
      return window.i18next.t('pmwjs_position');
    }
    if (currentlySelectedPanel === SlideShowPanels.INTRO_ANIMATION) {
      return window.i18next.t('pmwjs_entrance_animation');
    } else if (currentlySelectedPanel === SlideShowPanels.OUTRO_ANIMATION) {
      return window.i18next.t('pmwjs_exit_animation');
    } else {
      return window.i18next.t('pmwjs_color');
    }
  };

  const getHeader = (): ReactElement<any> | undefined => {
    if (showDefaultOptions() && currentItemHeaderTab === ItemHeaderTab.EDIT) {
      return undefined;
    }
    if (currentlySelectedPanel === SlideShowPanels.FONT) {
      return undefined;
    }
    return (
      <ContextualMenuHeader id="text-slide-subpanels-header">
        <ContextualMenuTextHeader
          showBackButton
          onBackButton={(): void => {
            if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
              setCurrentItemHeaderTab(ItemHeaderTab.EDIT);
            }
            setCurrentlySelectedPanel(SlideShowPanels.DEFAULT);
          }}
          text={getHeaderText()}
        />
      </ContextualMenuHeader>
    );
  };

  const getEditOptions = (): ReactElement<any> => {
    return (
      <>
        <ItemLockInformationBox />
        <PosterItemControls
          onClickPosition={(): void => {
            setCurrentItemHeaderTab(ItemHeaderTab.POSITION);
          }}
        />
        <BaseSlideshowEditOptions onOpenPanel={setCurrentlySelectedPanel} />
      </>
    );
  };

  const getDefaultOptions = (): ReactElement<any> => {
    switch (currentItemHeaderTab) {
      case ItemHeaderTab.EDIT:
        return getEditOptions();
      case ItemHeaderTab.POSITION:
        return <ItemPositionContextualOptions />;
      default:
        throw new Error('unhandled tab item for contextual header');
    }
  };

  const showDefaultOptions = (): boolean => {
    return currentlySelectedPanel === SlideShowPanels.DEFAULT;
  };

  const closePanel = (): void => {
    setCurrentlySelectedPanel(SlideShowPanels.DEFAULT);
  };

  const getTextSlideSubPanels = (): ReactElement<any> | null => {
    switch (currentlySelectedPanel) {
      case SlideShowPanels.BACKGROUND_COLOR:
        return <TextSlideBackgroundColorPanel />;
      case SlideShowPanels.SHADOW:
        return <SlideitemCustomShadowColorPanel />;
      case SlideShowPanels.TEXT_FILL:
        return <TextslideFillColorPanel />;
      case SlideShowPanels.OUTLINE:
        return <TextslideOutlineColorPanel />;
      case SlideShowPanels.INTRO_ANIMATION:
        return <ItemIntroAnimationPanel />;
      case SlideShowPanels.OUTRO_ANIMATION:
        return <ItemOutroAnimationPanel />;
      case SlideShowPanels.FONT:
        return (
          <TextSlideFontSelector
            onBack={(): void => {
              setCurrentlySelectedPanel(SlideShowPanels.DEFAULT);
            }}
          />
        );
      case SlideShowPanels.LIST_COLOR:
        return <TextSlideListColorPanel />;
      default:
        return null;
    }
  };

  const getMediaSlideSubPanels = (): ReactElement<any> | null => {
    switch (currentlySelectedPanel) {
      case SlideShowPanels.SHADOW:
        return <SlideitemCustomShadowColorPanel />;
      case SlideShowPanels.REMOVE_COLOR:
        return <SlideitemRemoveColorPanel />;
      case SlideShowPanels.TINT:
        return <SlideitemTintColorPanel />;
      case SlideShowPanels.MULTIPLY:
        return <SlideitemMutliplyColorPanel />;
      case SlideShowPanels.GLOW:
        return <SlideItemGlowColorPanel />;
      case SlideShowPanels.BLOCK_TRANSITION:
        return <SlideshowBlockTransitionColorPanel />;
      case SlideShowPanels.INTRO_ANIMATION:
        return <ItemIntroAnimationPanel />;
      case SlideShowPanels.OUTRO_ANIMATION:
        return <ItemOutroAnimationPanel />;
      default:
        return null;
    }
  };

  const getSubPanels = (): ReactElement<any> | null => {
    if (activeSlide?.isTextSlide()) {
      return getTextSlideSubPanels();
    }

    return getMediaSlideSubPanels();
  };

  const getOptions = (): ReactElement<any> | null => {
    if (showDefaultOptions()) {
      return getDefaultOptions();
    }

    return getSubPanels();
  };

  const getKey = (): string => {
    if (showDefaultOptions()) {
      return currentItemHeaderTab === ItemHeaderTab.POSITION ? ContextualMenuContentKeys.SLIDESHOW_DEFAULT_POSITION : ContextualMenuContentKeys.SLIDESHOW_DEFAULT_EDIT;
    }

    return '';
  };

  return useMemo((): ReactElement<any> => {
    return (
      <ContextualMenu
        id="slideshow-contextual-options"
        contentClassName={currentlySelectedPanel === SlideShowPanels.FONT ? 'spacing-p-0' : ''}
        header={getHeader()}
        includeLeftRightPadding={showDefaultOptions()}
        contentKey={getKey()}
      >
        {getOptions()}
      </ContextualMenu>
    );
  }, [currentItemHeaderTab, currentlySelectedPanel]);
}
